/** The pure half of the Checks tab: how a check's state is worded, how checks group into the four
 * parts, and where a fix item goes.
 *
 * **The server names the states and the parts; this words them.** Six states, none collapsed: a
 * check nobody ran (`not_evaluated`) and one waiting on another (`waiting`) are not passes, and
 * `not_applicable` is not in the count the server prints. The one percentage is the server's
 * (`summary.readiness_percent`, ADR-0212 decision 6), printed and never computed here.
 *
 * **A fix item is a pointer at an act that exists** (ADR-0163). It is followed only through an id
 * the server issued: a target that is not a uuid sends the reader to the list the item belongs to.
 * Whether *this reader's role* may take the act is the server's answer (`permitted`), and a role
 * that cannot is told so rather than shown a link that predictably refuses (design rule 6).
 */

import type { FilingCheck, FilingCheckFix, FilingCheckPart } from "../api/filingChecks";
import type { ChipTone } from "../components/ui/StatusChip";
import { STATUS_ORDER } from "./scopeTable/model";

export type CheckState = FilingCheck["state"];

export const CHECK_STATES = [
  "blocking",
  "not_evaluated",
  "waiting",
  "advisory",
  "passed",
  "not_applicable",
] as const;

export const CHECK_WORD: Record<string, { word: string; tone: ChipTone }> = {
  blocking: { word: "Blocking", tone: "crit" },
  not_evaluated: { word: "Not run", tone: "info" },
  waiting: { word: "Waiting", tone: "neutral" },
  advisory: { word: "Advisory", tone: "warn" },
  passed: { word: "Passed", tone: "ok" },
  not_applicable: { word: "Not applicable", tone: "neutral" },
};

/** A state this screen has never heard of is drawn as itself, in neutral, and counted as open. */
export function wordOf(state: string): { word: string; tone: ChipTone } {
  return CHECK_WORD[state] ?? { word: state.replace(/_/g, " "), tone: "neutral" };
}

/** Whether a check still needs somebody: anything that is not a pass and not out of scope. */
export function isOpen(check: Pick<FilingCheck, "state">): boolean {
  return check.state !== "passed" && check.state !== "not_applicable";
}

export interface PartGroup {
  part: FilingCheckPart;
  checks: FilingCheck[];
}

/** The checks under their parts, in the order the server sent the parts, worst state first within a
 * part. A check whose part the server did not list is kept in a part of its own, never dropped. */
export function groupByPart(
  parts: readonly FilingCheckPart[],
  checks: readonly FilingCheck[],
): PartGroup[] {
  const order = new Map<string, number>(CHECK_STATES.map((state, index) => [state, index]));
  const rank = (check: FilingCheck) => order.get(check.state) ?? 0;
  const groups: PartGroup[] = parts.map((part) => ({ part, checks: [] }));
  for (const check of checks) {
    let group = groups.find((entry) => entry.part.part === check.part);
    if (!group) {
      group = {
        part: {
          part: check.part,
          label: check.part_label,
          passed: 0,
          total: 0,
          not_run: 0,
          state: "not_evaluated",
        },
        checks: [],
      };
      groups.push(group);
    }
    group.checks.push(check);
  }
  for (const group of groups) group.checks.sort((a, b) => rank(a) - rank(b));
  return groups;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const idOf = (value: string | undefined): string | undefined =>
  value !== undefined && UUID.test(value) ? value : undefined;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const isoDay = (value: string | undefined): value is string => value !== undefined && ISO_DAY.test(value);

export interface FixLink {
  label: string;
  /** A path inside the app, or `"disclosures"` for the tab of this same filing. */
  to: string;
  /** Present when the act is on another tab of the filing being read. */
  tab?: string;
  /** Search parameters the destination needs (the register's filter and window), carried beside the
   * reader's own. Only values the model checked: a status the register offers, an ISO date. */
  search?: Record<string, string>;
}

/** Where a fix item goes, by the act it names. The closed list is the server's `FixAction`. */
export function fixLinkFor(fix: Pick<FilingCheckFix, "action" | "target">): FixLink {
  const target = fix.target;
  switch (fix.action) {
    case "upload_document":
      return { label: "Add the data", to: "/evidence" };
    case "assign_collector": {
      const source = idOf(target.expected_source_id);
      return { label: "Name who collects it", to: source ? `/sources/${source}` : "/sources" };
    }
    case "declare_nil": {
      const source = idOf(target.expected_source_id);
      return { label: "Declare or add", to: source ? `/sources/${source}` : "/sources" };
    }
    case "verify_document": {
      const document = idOf(target.document_id);
      return { label: "Review the document", to: document ? `/evidence/${document}` : "/evidence" };
    }
    case "record_lease": {
      const site = idOf(target.facility_id);
      return { label: "Answer the question", to: site ? `/organisation/sites/${site}` : "/organisation" };
    }
    case "explain_difference":
      return { label: "Explain the difference", to: "/prior-disclosures" };
    case "write_statement":
      return { label: "Write the statement", to: "/reports", tab: "disclosures" };
    case "accept_figure": {
      // A figure is accepted on its own record page (ADR-0067). An item with no record is the
      // "and N more" one: it opens the register, filtered to what is awaiting acceptance, in the
      // filing's own window - the reader's window may be another year, and a register for a different
      // year would show figures this filing does not state.
      const record = idOf(target.record_id);
      if (record) return { label: "Accept the figure", to: `/records/${record}` };
      const search: Record<string, string> = {};
      if (target.register_status !== undefined && (STATUS_ORDER as readonly string[]).includes(target.register_status)) {
        search.status = target.register_status;
      }
      if (isoDay(target.period_start)) search.from = target.period_start;
      if (isoDay(target.period_end)) search.to = target.period_end;
      return { label: "Open the register", to: "/emissions", ...(Object.keys(search).length > 0 ? { search } : {}) };
    }
    case "request_approval": {
      const record = idOf(target.record_id);
      return { label: "Send for approval", to: record ? `/records/${record}` : "/approvals", tab: "workflow" };
    }
    default:
      return { label: "Open", to: "/tasks" };
  }
}

/** The checks a reader has to do something about, worst first: blocking, then advisory, each in the
 * server's order. A check that is waiting or was not run is counted in the summary and is not a
 * finding: it has not failed. This is the one list the filing's Findings view and its page share. */
export function failingChecks(checks: readonly FilingCheck[]): FilingCheck[] {
  return [
    ...checks.filter((check) => check.state === "blocking"),
    ...checks.filter((check) => check.state === "advisory"),
  ];
}

/** The one sentence of counts the page and every view of the filing print, from the server's summary
 * and its per-state counts (design review 2026-11-03: the page said three checks blocked while its
 * Disclosures view said none did, because the second counted a different list). Nothing is summed
 * here: each number is the server's. */
export function countsLine(read: {
  summary: { passed: number; total: number; not_run: number };
  counts: Readonly<Record<string, number>>;
}): string {
  const blocking = read.counts.blocking ?? 0;
  const advisory = read.counts.advisory ?? 0;
  const { passed, total, not_run } = read.summary;
  const notRun = not_run > 0 ? `, ${String(not_run)} not run yet` : "";
  return `${String(blocking)} blocking, ${String(advisory)} advisory · ${String(passed)} of ${String(total)} checks passed${notRun}`;
}

/** The role a fix needs, said for a reader who cannot take it. The server's `permitted` is the
 * authority; this only words the refusal. */
export const NOT_PERMITTED = "Your role cannot take this step.";
